"use client";

import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CheckoutDialog from "@/components/CheckoutDialog";
import {
  Check,
  Info,
  HelpCircle,
  Lock,
  ShieldCheck,
  BadgeCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { LEGAL, PLANS, type PlanId } from "@/lib/legal";

const faqs = [
  {
    q: "¿Puedo cambiar de plan en cualquier momento?",
    a: "Sí, puedes actualizar o degradar tu plan al inicio de cada ciclo de facturación mensual.",
  },
  {
    q: "¿Cómo pago de forma segura?",
    a: `Los pagos con tarjeta se procesan en el TPV virtual de Banco Sabadell (Redsys). ${LEGAL.companyName} no almacena el número de tarjeta ni el CVV.`,
  },
  {
    q: "¿Qué es una reserva?",
    a: "Puedes reservar un plan sin pagar de inmediato. Te confirmamos disponibilidad y te enviamos el enlace de cobro cuando quieras activarlo (validez orientativa: 7 días).",
  },
  {
    q: "¿Los precios incluyen IVA?",
    a: "No. Los importes publicados no incluyen IVA. Se aplicará el impuesto vigente en la factura emitida por Xappiens BI SL.",
  },
];

export default function PricingPage() {
  const [selectedPlan, setSelectedPlan] = useState<PlanId | null>(null);

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <section className="pt-32 pb-16 px-6 bg-gradient-to-b from-primary/5 to-background">
        <div className="container mx-auto text-center max-w-4xl">
          <span className="text-primary text-sm font-medium tracking-[0.2em] uppercase mb-4 block">
            Tarifas transparentes
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
            Inversión inteligente, <br />
            <span className="text-primary">listo para comprar o reservar</span>
          </h1>
          <p className="text-xl text-muted-foreground mb-6 text-balance">
            Elige el nivel de exposición adecuado para tu negocio. Pago seguro
            con TPV Banco Sabadell o reserva sin compromiso.
          </p>
          <p className="text-sm text-muted-foreground">
            Servicio prestado por <strong className="text-foreground">{LEGAL.companyName}</strong>{" "}
            · CIF {LEGAL.cif} · Precios sin IVA
          </p>
        </div>
      </section>

      <section className="pb-8 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="grid sm:grid-cols-3 gap-4 rounded-xl border border-border bg-card p-4 md:p-5">
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold">TPV Banco Sabadell</p>
                <p className="text-xs text-muted-foreground">
                  Cobro cifrado vía Redsys
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold">Datos protegidos</p>
                <p className="text-xs text-muted-foreground">
                  Sin almacenar PAN ni CVV
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <BadgeCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold">Factura oficial</p>
                <p className="text-xs text-muted-foreground">
                  Emitida por {LEGAL.companyName}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 px-6">
        <div className="container mx-auto max-w-7xl">
          <div className="grid md:grid-cols-3 gap-8 mb-24">
            {PLANS.map((plan) => (
              <Card
                key={plan.id}
                className={`flex flex-col border-2 transition-all duration-300 hover:shadow-xl ${
                  plan.highlight
                    ? "border-primary shadow-lg bg-primary/5 relative scale-105 z-10"
                    : "border-border hover:border-primary/30 bg-card"
                }`}
              >
                {plan.highlight && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full tracking-wide shadow-sm">
                    MÁS POPULAR
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-foreground">
                    {plan.name}
                  </CardTitle>
                  <CardDescription className="text-muted-foreground mt-2 min-h-[40px]">
                    {plan.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-grow">
                  <div className="mb-2 flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-foreground">
                      {plan.priceLabel}
                    </span>
                    {plan.period && (
                      <span className="text-muted-foreground">{plan.period}</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mb-6">
                    {plan.vatNote}
                  </p>

                  <ul className="space-y-3">
                    {plan.features.map((feature) => (
                      <li
                        key={feature}
                        className="flex items-start gap-3 text-sm text-foreground/80"
                      >
                        <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter className="pt-4">
                  {plan.action === "checkout" ? (
                    <Button
                      className={`w-full font-semibold ${
                        plan.highlight
                          ? "bg-primary text-primary-foreground hover:bg-primary/90"
                          : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                      }`}
                      size="lg"
                      onClick={() => setSelectedPlan(plan.id)}
                    >
                      {plan.cta}
                    </Button>
                  ) : (
                    <Button
                      asChild
                      className="w-full font-semibold bg-secondary text-secondary-foreground hover:bg-secondary/80"
                      size="lg"
                    >
                      <a href="/#contacto">{plan.cta}</a>
                    </Button>
                  )}
                </CardFooter>
              </Card>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-12 lg:gap-24 mb-20 items-start">
            <div>
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <Info className="w-6 h-6 text-primary" />
                ¿Por qué {LEGAL.brand}?
              </h3>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <span className="text-xl font-bold text-primary">1</span>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Red de calidad</h4>
                    <p className="text-muted-foreground text-sm">
                      Nuestros portales no son granjas de enlaces. Son medios
                      temáticos con editores reales y audiencias fieles.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <span className="text-xl font-bold text-primary">2</span>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Cobro bancario seguro</h4>
                    <p className="text-muted-foreground text-sm">
                      Integración preparada para TPV Banco Sabadell. Condiciones,
                      privacidad y cookies publicadas para una contratación
                      transparente.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <span className="text-xl font-bold text-primary">3</span>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Empresa identificada</h4>
                    <p className="text-muted-foreground text-sm">
                      {LEGAL.companyName}, CIF {LEGAL.cif}, {LEGAL.address}.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <HelpCircle className="w-6 h-6 text-primary" />
                Preguntas frecuentes
              </h3>
              <div className="space-y-4">
                {faqs.map((faq) => (
                  <div
                    key={faq.q}
                    className="p-4 rounded-lg bg-card border border-border"
                  >
                    <h4 className="font-semibold mb-2 text-foreground">
                      {faq.q}
                    </h4>
                    <p className="text-sm text-muted-foreground">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p className="text-center text-xs text-muted-foreground max-w-2xl mx-auto">
            Al contratar aceptas nuestros{" "}
            <a href="/terminos" className="underline underline-offset-2">
              términos y condiciones
            </a>
            ,{" "}
            <a href="/privacidad" className="underline underline-offset-2">
              privacidad
            </a>{" "}
            y{" "}
            <a href="/cookies" className="underline underline-offset-2">
              cookies
            </a>
            . Ver también el{" "}
            <a href="/aviso-legal" className="underline underline-offset-2">
              aviso legal
            </a>
            .
          </p>
        </div>
      </section>

      <CheckoutDialog
        planId={selectedPlan}
        open={selectedPlan !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedPlan(null);
        }}
      />

      <Footer />
    </div>
  );
}
